import {
  onCartUpdate,
  addItemsToCartRequest,
  maxQuantityHandler
} from '@/js/utils/cart-utilities.js';
import { clearBundle, resetBundleId } from '../../src/js/bundle-store';
import { errorMessage } from '@/js/utils/error-message.js';
import { addNotification } from '@/js/bundle-notifications-store';

/**
 * Click event when adding to cart - adds all the data from the form which is useful for recharge products to retrieve the selling plan.
 * Note: Should be used on the PDP when the full form is available in the HTML
 *
 * TODO: Review refactoring this and `addItemToCart` into a single method
 * @returns {void}
 */
async function addItemFromFormToCart(form, button, additionalSections = []) {
  const { alreadyAddedText, alterationText } =
    globalThis.PxlApp.translations.productErrors;

  const flyoutCart = document.querySelector('flyout-cart');
  const sectionsRequired = [
    flyoutCart.dataset.sectionId,
    ...additionalSections
  ];

  const ageIsVerified = await doAgeVerification();
  if (!ageIsVerified) return;

  try {
    button.classList.add('btn-loading');
    const properties = addKyotoOnlyProductProperties(button);
    let productData = Object.fromEntries(form.entries());
    productData = { ...productData, properties };
    const variantId = productData.id;
    const quantity = Number(productData.quantity);

    // eslint-disable-next-line no-console
    console.info(
      `addItemFromFormToCart properties: ${JSON.stringify(properties)}`
    );

    // Get the max inventory quantity from the button and check the requested quantity against it - if it's greater, set the quantity to the max inventory quantity
    const maxInventoryQuantity = Number(
      button.getAttribute('max-inventory-quantity')
    );

    const trackQuantity = Object.hasOwn(button.dataset, 'trackQuantity');

    const quantityToAdd = await maxQuantityHandler(
      quantity,
      maxInventoryQuantity,
      variantId,
      trackQuantity
    );

    const errorMessageWrapper = button.nextElementSibling;
    const errorContent = errorMessageWrapper.querySelector(
      '[data-error-message]'
    );

    if (quantityToAdd <= 0) {
      // Don't add the item to the cart if the quantityToAdd is 0 or less
      await updateButtonState(button, 'error');

      if (maxInventoryQuantity) {
        const message = alreadyAddedText.replace(
          '{quantity}',
          maxInventoryQuantity
        );
        await errorMessage(errorContent, message);
      }

      return;
    } else if (quantityToAdd !== quantity) {
      await updateButtonState(button, 'error');
      await errorMessage(errorContent, alterationText);
    }

    // Modify the quantity in the productData to the quantityToAdd
    productData.quantity = quantityToAdd;

    const response = await addItemsToCartRequest(
      [productData],
      sectionsRequired
    );

    if (response.status == 200) {
      await updateButtonState(button, 'success');
      await emitAddToCartSuccessEvent();
    } else {
      await updateButtonState(button, 'error');
      // eslint-disable-next-line no-console
      console.error('Error adding product to cart');
    }

    const responseJson = await response.json();
    await onCartUpdate(responseJson);
  } catch (error) {
    await updateButtonState(button, 'error');
    // eslint-disable-next-line no-console
    console.error(`Error adding product to cart: ${JSON.stringify(error)}`);
  }
}

/**
 * Adds a variant to the cart
 * Note: Should be used in quick add, bundle builder, and multi-add
 *
 * TODO: Review refactoring this and `addItemFromFormToCart` into a single method
 *
 * @param {string} variantId - The ID of the variant to add to the cart
 * @param {number} quantity - The quantity of the variant to add to the cart
 * @param {HTMLElement} button - The button that was clicked to add the product to the cart
 * @param {HTMLElement} errorContent - The error message element
 * @param {Array{String}} additionalSections - Additional sections to update when the cart is updated
 *
 * @returns {void}
 */
async function addItemToCart(
  variantId,
  quantity = 1,
  button,
  errorContent,
  customElementName,
  additionalSections = []
) {
  const { alterationText, alreadyAddedText, noAvailabilityText } =
    globalThis.PxlApp.translations.productErrors;
  const flyoutCart = document.querySelector('flyout-cart');
  const sectionsRequired = [
    flyoutCart.dataset.sectionId,
    ...additionalSections
  ];

  const ageIsVerified = await doAgeVerification();
  if (!ageIsVerified) return;

  try {
    button.classList.add('btn-loading');

    // Get the max inventory quantity from the button and check the requested quantity against it - if it's greater, set the quantity to the max inventory quantity
    let maxInventoryQuantity = Number(
      button.getAttribute('max-inventory-quantity')
    );

    const trackQuantity = Object.hasOwn(button.dataset, 'trackQuantity');
    const quantityToAdd = await maxQuantityHandler(
      quantity,
      maxInventoryQuantity,
      variantId,
      trackQuantity
    );

    // Don't add the item to the cart if the quantityToAdd is 0 or less
    if (quantityToAdd <= 0) {
      await updateButtonState(button, 'error');

      if (maxInventoryQuantity) {
        const message = alreadyAddedText.replace(
          '{quantity}',
          maxInventoryQuantity
        );
        await errorMessage(errorContent, message);
      }
      return;
    } else if (quantityToAdd !== quantity) {
      await updateButtonState(button, 'error');

      if (customElementName === 'product-multi-add') {
        await errorMessage(errorContent, noAvailabilityText);

        return;
      } else {
        await errorMessage(errorContent, alterationText);
      }
    }

    const properties = addKyotoOnlyProductProperties(button);
    // eslint-disable-next-line no-console
    console.info(`addItemToCart properties: ${JSON.stringify(properties)}`);

    const items = [
      { id: variantId, quantity: quantityToAdd, properties: properties }
    ];
    const response = await addItemsToCartRequest(items, sectionsRequired);

    await updateButtonState(button, 'success');
    await emitAddToCartSuccessEvent();
    const responseJson = await response.json();
    await onCartUpdate(responseJson);
  } catch (error) {
    await updateButtonState(button, 'error');
    // eslint-disable-next-line no-console
    console.error(`Error adding product to cart: ${JSON.stringify(error)}`);
  }
}

/**
 * Adds all the items in the bundle to the cart
 *
 * @param {Event} event - The click event
 * @param {[Object]} bundleItems - The items to add to the cart
 * @param {string}  bundleId - The generated bundle Id for the bundle to add to cart
 * @param {Integer}  itemsCount - The total items added to the bundle
 *
 * @returns {void}
 */
// TODO: Review function size for refactor -
// See https://github.com/pixelcabin/kbc-theme/pull/88#discussion_r1439649283
// See https://github.com/pixelcabin/kbc-theme/pull/88/files#r1406145809
async function addBundleToCart(
  event,
  bundleItems,
  bundleId,
  itemsCount,
  boxCount
) {
  const ageIsVerified = await doAgeVerification();
  if (!ageIsVerified) return;

  const button = event.target;
  if (!button || !bundleItems) return;

  button.classList.add('btn-loading');

  const items = bundleItems.value.map(item => {
    return {
      id: Number(item.variantId),
      quantity: Number(item.quantity),
      properties: {
        _bundle_id: bundleId.value,
        _items_count: Number(itemsCount.value),
        _box_count: Number(boxCount.value),
        _kyoto_only: Boolean(item.kyotoOnlyProduct)
      }
    };
  });

  if (!items) {
    throw new Error(`Bundle is empty!?`);
  }

  try {
    let hasOneOrMoreItemErrors = false;
    await Promise.all(
      bundleItems.value.map(async item => {
        try {
          const quantityToAdd = await maxQuantityHandler(
            Number(item.quantity),
            Number(item.maxInventoryQuantity),
            item.variantId,
            item.trackQuantity,
            item.bundleable,
            true
          );

          if (quantityToAdd <= 0) {
            hasOneOrMoreItemErrors = true;
            // Add an error notification for the item
            addNotification({
              title: item.title,
              type: 'errorMessage',
              number: item.maxInventoryQuantity
            });
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(`Error processing item ${item.title}:`, error);
        }
      })
    );

    if (hasOneOrMoreItemErrors) {
      button.classList.remove('btn-loading');
    } else {
      const flyoutCart = document.querySelector('flyout-cart');

      const response = await addItemsToCartRequest(items, [
        flyoutCart.dataset.sectionId
      ]);

      button.classList.remove('btn-loading');

      const cartResponseJson = await response.json();
      onCartUpdate(cartResponseJson);

      if (response.status == 200) {
        button.classList.add('btn-success');
        await emitAddToCartSuccessEvent();
        button.disabled = true;

        setTimeout(() => {
          button.classList.remove('btn-success');
          button.disabled = false;
          clearBundle();
        }, 500);
      } else {
        resetBundleId();
        button.classList.add('btn-error');
        setTimeout(() => {
          button.classList.remove('btn-error');
        }, 1000);
      }
    }
  } catch (error) {
    resetBundleId();
    // eslint-disable-next-line no-console
    console.error('Error:', error);
  }
}

/**
 * Update the state of the button
 * @param {object} button - The button to update the state of
 * @param {string} state - The state to set the button to
 */
async function updateButtonState(button, state) {
  const buttonText = button.textContent;

  button.classList.remove('btn-loading');
  button.classList.add(`btn-${state}`);
  button.textContent = buttonText;

  setTimeout(() => {
    button.classList.remove(`btn-${state}`);
    button.textContent = buttonText;
  }, 1000);
}

/**
 * Verify age if age verification element is present
 * @returns {boolean}
 */
async function doAgeVerification() {
  const ageVerificationElement = document.querySelector('age-verification');
  if (!ageVerificationElement) return true;
  return ageVerificationElement.verifyAge();
}

/**
 * Emit an event when a product is added to the cart
 * @returns {void}
 */
async function emitAddToCartSuccessEvent() {
  const event = new CustomEvent('pxl:addToCartSuccess', {
    bubbles: true
  });

  document.dispatchEvent(event);
}

/**
 *
 * @param {object} button - The button that was clicked to add the product to the cart
 * @returns {object} - The hidden properties to add to the product
 */
function addKyotoOnlyProductProperties(button) {
  const canOnlyShipToKyoto = button.hasAttribute('kyoto-only-product');
  return canOnlyShipToKyoto
    ? {
        _kyoto_only: true
      }
    : {};
}

export {
  addItemToCart,
  addBundleToCart,
  doAgeVerification,
  addItemFromFormToCart,
  updateButtonState
};
